import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import CardText from './CardText';
import CardImage from './CardImage';
import { CardContext } from '~/contexts/CardContext';
import { getCardContent } from '~/utilities/getCardContent';
import { getCardImageWidths } from '~/utilities/getCardImageWidths';
import AnalyticsClick from '~/components/Shared/Analytics/AnalyticsClick';
import { getOneIndexing } from '~/utilities/getOneIndexing';
import AnalyticsContainer from '~/components/Shared/Analytics/AnalyticsContainer';
import JsonLd from '~/components/Shared/Utility/JsonLd/JsonLd';

const containerClasses = cva(
  'card relative box-border transition duration-300 group/card',
  {
    variants: {
      showBorder: {
        true: 'border border-solid border-lightGrey',
      },
      rounded: {
        true: 'rounded-2xl overflow-hidden',
      },
      metaPosition: {
        bottom: 'flex gap-2.5 flex-col',
        top: 'flex gap-2.5 flex-col-reverse',
        right: '',
        left: 'flex gap-4 flex-row-reverse',
        overlay: '',
      },
      isHero: {
        true: '',
      },
      isSpotlight: {
        true: '',
      },
      showDescription: {
        true: '',
      },
    },
    compoundVariants: [
      {
        isHero: true,
        isSpotlight: false,
        // There was a class here that made the aspect ratio a square in mobile that was removed to fix SI Swimsuit
        // Not sure if that was needed for something else
        class: 'lg:max-h-[650px] overflow-hidden',
      },
      {
        metaPosition: 'right',
        isSpotlight: false,
        showDescription: false,
        class: 'flex flex-row gap-4',
      },
      {
        metaPosition: 'right',
        isSpotlight: true,
        class: 'flex flex-col md:flex-row gap-4 !border-borderGrey',
      },
      {
        metaPosition: 'right',
        isSpotlight: false,
        showDescription: true,
        class: 'flex flex-col sm:flex-row gap-3 sm:gap-4',
      },
    ],
  }
);

const textWrapperClasses = cva('flex flex-col flex-1', {
  variants: {
    metaPosition: {
      bottom: '',
      top: '',
      right: '',
      left: '',
      overlay:
        'px-5 pb-4 pt-10 absolute left-0 right-0 bottom-0 overflow-hidden text-white [&_.card-footer]:!text-lightGrey bg-gradient-to-b from-[rgba(0,0,0,0)] to-black',
    },
    metaAlignment: {
      left: 'items-start text-left',
      center: 'items-center text-center',
    },
    isSpotlight: {
      true: 'px-5 pb-5 md:p-0 md:justify-center md:ml-2.5 md:mr-5',
      false: '',
    },
    isHero: {
      true: 'gap-3',
      false: 'gap-[5px]',
    },
    mobileFullWidth: {
      true: '',
    },
  },
  compoundVariants: [
    {
      metaPosition: 'overlay',
      mobileFullWidth: true,
      isHero: false,
      class: '-mx-5 md:mx-0',
    },
  ],
});

const imageWrapperClasses = cva('relative', {
  variants: {
    imageWidth: {
      full: '',
      half: 'md:w-1/2',
      small: '',
      medium: '',
      '3/12': '',
    },
    mobileFullWidth: {
      true: '-mx-5 md:mx-0',
    },
  },
});

const imageClass = cva('min-w-full object-cover object-center', {
  variants: {
    isHero: {
      true: '',
    },
    isSpotlight: {
      true: '',
    },
  },
  compoundVariants: [
    {
      isHero: true,
      isSpotlight: false,
      // There was a class here that made the aspect ratio a square in mobile that was removed to fix SI Swimsuit
      // Not sure if that was needed for something else
      class: 'lg:max-h-[650px]',
    },
  ],
});

export default component$(({ content, index, ...props }: any): JSX.Element => {
  const {
    imageWidth: originalImageWidth,
    imageAspectRatio,
    metaPosition,
    metaAlignment,
    showImage,
    showDescription,
    showAuthor,
    showDate,
    showTitle = true,
    showBorder,
    rounded,
    showMainCategory,
    columnWidth,
    datePosition,
    mobileFullWidth,
    rows,
    columns,
    titleSize,
    descriptionSize,
  } = useContext(CardContext);

  const isHorizontal = metaPosition === 'right' || metaPosition === 'left';
  const imageWidth = isHorizontal ? originalImageWidth : 'full';
  const isSpotlight = imageWidth === 'half' && columnWidth === 'full';
  const isSingle = rows === 1;
  const isHero = rows === 1 && columns === 1;
  const imageWidths = getCardImageWidths(
    imageWidth,
    imageAspectRatio,
    columnWidth,
    columns,
    isHero
  );

  const cardContent = getCardContent(content, props);
  if (!cardContent) {
    return <></>;
  }

  const {
    cardLink,
    title,
    formattedDate,
    seoDescription,
    cover,
    coverType,
    mainCategory,
    authors,
  } = cardContent;

  const componentName = `Card-${getOneIndexing(index)}`;

  // Maybe for manual card should be separate component, it is not an article
  return (
    <article
      class={containerClasses({
        metaPosition,
        showBorder,
        rounded,
        isSpotlight,
        showDescription,
        isHero,
      })}
    >
      <JsonLd type='articleEach' data={cardContent} />
      <AnalyticsContainer
        component_name={componentName}
        component_type='Card'
        component_placement={getOneIndexing(index)}
      >
        {showImage && (
          <CardImage
            cover={cover}
            coverType={coverType}
            imageWidths={imageWidths}
            isSingle={isSingle}
            imageAspectRatio={imageAspectRatio}
            wrapperClass={imageWrapperClasses({
              imageWidth,
              mobileFullWidth,
            })}
            imageClass={imageClass({
              isHero,
              isSpotlight,
            })}
          />
        )}

        <CardText
          title={title}
          description={seoDescription}
          showTitle={showTitle}
          showDescription={showDescription}
          authors={authors}
          showAuthor={showAuthor}
          date={formattedDate}
          showDate={showDate}
          datePosition={datePosition}
          showImage={showImage}
          mainCategory={mainCategory}
          showMainCategory={showMainCategory}
          isSingle={isSingle}
          titleSize={titleSize}
          descriptionSize={descriptionSize}
          wrapperClass={textWrapperClasses({
            metaPosition,
            metaAlignment,
            isSpotlight,
            mobileFullWidth,
            isHero,
          })}
        />

        <AnalyticsClick
          click_link_url={cardLink ?? ''}
          link_position={getOneIndexing(index)}
          wrapperClass='absolute inset-0 w-full h-full'
        >
          <a
            href={cardLink}
            class='absolute inset-0 w-full h-full'
            title={title}
            aria-labelledby={title}
          />
        </AnalyticsClick>
      </AnalyticsContainer>
    </article>
  );
});
